import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  SvgIconOwnProps,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { HidroLayer, HidroLayerGroup, HidroLayersComponent } from "./layers";
import {
  ChangeCircleOutlined,
  CircleOutlined,
  Close,
  Delete,
  Edit,
  FolderZipOutlined,
  HexagonOutlined,
  JavascriptTwoTone,
  MyLocation,
  PinDrop,
  PolylineOutlined,
  RectangleOutlined,
  RoundedCornerOutlined,
  Save,
  Timeline,
  Title
} from "@mui/icons-material";
import React, {
  JSX,
  useEffect,
  useRef,
  useState
} from "react";
import { HidroMap } from "./mapa";
import { googleHibrido } from "./tiles";
import { GeometryImportTypes, ImportGeometry } from "./importGeometry";
import { exportGeoJSON, geometryParser } from "./parseGeometry";
import {
  LatLng,
  Map,
  Marker,
  PM,
  Polygon,
} from "leaflet";
import NumberInput from "./numberInput";
import "@geoman-io/leaflet-geoman-free";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";

export interface GeometryEditorError {
  error: string;
  message: string;
}

export interface GeometryEditorProps extends Omit<Omit<DialogProps, 'children'>, 'fullScreen'> {
  initialLayers?: HidroLayerGroup;
  onFinish?: (layers: HidroLayerGroup) => void;
  delineateWatershed?: (latLng: LatLng) => Promise<HidroLayer | HidroLayerGroup | GeometryEditorError>;
}

export const GeometryEditor: React.FC<GeometryEditorProps> = ({
  onFinish,
  initialLayers,
  delineateWatershed,
  ...modalProps
}) => {
  const theme = useTheme();

  const iconProps: SvgIconOwnProps = {
    sx: {
      width: "20px",
      height: "20px",
      color: theme.palette.getContrastText(theme.palette.secondary.main),
    }
  };
  const disableIconProps: SvgIconOwnProps = {
    sx: {
      width: "20px",
      height: "20px",
      color: "rgba(0, 0, 0, 0.3)",
    }
  };
  const editingIconProps: SvgIconOwnProps = {
    sx: {
      width: "20px",
      height: "20px",
      color: "rgb(0, 0, 0)",
    }
  };

  const layers = useRef(initialLayers ?? new HidroLayerGroup({
    name: "Layers",
    showing: true,
    layers: [
      new HidroLayer({
        name: "Mapa",
        showing: true,
        layer: googleHibrido(),
      })
    ]
  }));

  const [map, setMap] = useState<Map | undefined>();
  const [toolbarPosition, setToolbarPosition] = useState<"row" | "column">("column");

  const [loading, setLoading] = useState<string | undefined>();
  const [error, setError] = useState<GeometryEditorError | undefined>();

  const [geomEditing, setGeomEditing] = useState<L.Layer | undefined>();
  const [isEditing, setIsEditing] = useState<boolean | undefined>();
  const [importType, setImportType] = useState<GeometryImportTypes | undefined>();
  const [watershedPoint, setWatershed] = useState<LatLng | undefined>();

  useEffect(() => {
    layers.current.setActions({
      onClick: (layer) => {
        resetVariables();
        setGeomEditing(layer);
      }
    });
  }, [layers.current]);

  useEffect(() => {
    if (!map) return;

    map.on('pm:create', (e) => {
      map.removeLayer(e.layer);
      const layer = new HidroLayer({
        name: e.shape,
        showing: true,
        layer: e.layer,
      });

      if (layer.layer instanceof Marker) {
        layer.setStyle({
          iconStr: "LocationOn",
          color: "#FF0",
        });
      }

      layer.setActions({
        onClick: (layer) => {
          resetVariables();
          setGeomEditing(layer);
        }
      });

      layers.current.push(layer);
    });

    // Ouvindo eventos de edição de geometrias
    map.on('pm:edit', (e) => {
      console.log("Geometria editada:", e.layer);
    });

    // Ouvindo eventos de remoção de geometrias
    map.on('pm:remove', (e) => {
      console.log("Geometria removida:", e.layer);
    });

    return () => {
      map.off('pm:create');
      map.off('pm:edit');
      map.off('pm:remove');
    };
  }, [map, layers.current]);

  const resetVariables = (
    data?: { geom?: boolean; edit?: boolean; importType?: boolean; watershed?: boolean; resetEditMap?: boolean; }
  ) => {
    if (data?.edit ?? true) setIsEditing(false);
    if (data?.geom ?? true) setGeomEditing(undefined);
    if (data?.watershed ?? true) setWatershed(undefined);
    if (data?.importType ?? true) setImportType(undefined);
    if (data?.resetEditMap ?? true) map?.pm.disableGlobalEditMode();
  };

  const handleCloseError = () => setError(undefined);

  const handleImportGeometry = (type: GeometryImportTypes) => {
    resetVariables();
    setImportType(type);
  };
  const handleOnImportGeometry = (geoms: (GeoJSON.GeoJsonObject | GeoJSON.GeoJsonObject[])[]) => {
    for (const geom of geoms) {
      if (!("lenght" in geom)) {
        const geoAdd = geometryParser(geom as GeoJSON.GeoJsonObject);
        if (geoAdd) {
          layers.current.push(geoAdd);
          geoAdd.setActions({
            onClick: (layer) => {
              resetVariables();
              setGeomEditing(layer);
            }
          });
        }
        continue;
      }

      for (const geom_ of geom as GeoJSON.GeoJsonObject[]) {
        const geoAdd = geometryParser(geom_);
        if (geoAdd) {
          layers.current.push(geoAdd);
          geoAdd.setActions({
            onClick: (layer) => {
              resetVariables();
              setGeomEditing(layer);
            }
          });
        }
      }
    }

    resetVariables();
  };

  const handleWatershed = (lat: number, lon: number, flush = false) => {
    if (!delineateWatershed) return;
    resetVariables();

    const latLng = new LatLng(lat, lon);
    if (flush) {
      setWatershed(undefined);
    } else {
      setWatershed(latLng);
    }

    let ponto = layers.current.getHidrolayerByLayer((layer) => {
      if (layer.layer instanceof Marker) {
        const lay = (layer.layer as Marker);
        if (lay.getLatLng() === watershedPoint) return true;
      }
      return false;
    });

    if (flush && ponto) {
      layers.current.removeLayer(ponto);
    } else if (!flush && !ponto) {
      ponto = new HidroLayer({
        showing: true,
        name: "Exutório",
        layer: new Marker(latLng),
        style: {
          iconStr: "Circle",
          color: "#F00"
        }
      });
      layers.current.push(ponto);
    }

    if (!flush && ponto) {
      ((ponto as HidroLayer).layer as Marker).setLatLng(latLng);
      (ponto as HidroLayer).centerBounds();
    }
  };
  const handleDelineateWatershed = async () => {
    if (!watershedPoint || !delineateWatershed) return;

    setLoading("Delimitando área de drenagem");
    const geom = await delineateWatershed(watershedPoint);
    setLoading(undefined);

    if ("error" in geom) {
      setError(geom);
      return;
    }
    layers.current.push(geom);
    geom.centerBounds();
    geom.setActions({
      onClick: (layer) => {
        resetVariables();
        setGeomEditing(layer);
      }
    });
    resetVariables();
    handleWatershed(0, 0, true);
  };

  const handleNewBBOX = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("Rectangle");
  };
  const handleNewCircle = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("Circle");
  };
  const handleNewPolygon = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("Polygon");
  };
  const handleNewPolyline = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("Line");
  };
  const handleNewMarker = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("Marker");
  };
  const handleNewCircleMarker = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("CircleMarker");
  };
  const handleNewText = () => {
    if (!map) return;
    resetVariables();
    map.pm.enableDraw("Text");
  };

  const handleEdit = () => {
    resetVariables({ geom: false });
    if (!geomEditing || !("pm" in geomEditing)) return;

    const enabled = (geomEditing.pm as PM.PMLayer).enabled();

    if (enabled) {
      setIsEditing(false);
      (geomEditing.pm as PM.PMLayer).disable();
    } else {
      setIsEditing(true);
      (geomEditing.pm as PM.PMLayer).enable({
        allowSelfIntersection: false,
        allowRotation: true,
        draggable: true,
      });
    }
  };
  const handleSave = () => {
    resetVariables({ geom: false });
    if (!geomEditing) return;
    if (!("toGeoJSON" in geomEditing)) {
      console.log(geomEditing);
      return;
    }

    const layer = layers.current.getHidrolayerByLayer(geomEditing);
    if (!layer) return;

    const geojson = (geomEditing as Polygon).toGeoJSON();
    exportGeoJSON(geojson, layer.name + ".geojson");
  };
  const handleRemove = () => {
    if (!geomEditing) return;
    const removed = layers.current.removeLayer(geomEditing);
    if (removed) setGeomEditing(undefined);
    resetVariables();
  };

  let geomInformation: HidroLayer | undefined = undefined;
  if (geomEditing && layers.current) {
    const layer = layers.current.getHidrolayerByLayer(geomEditing);
    if (layer) geomInformation = layer;
  }

  return <>
    {/* Alerta de Erro */}
    {error && <Snackbar
      open={Boolean(error)}
      autoHideDuration={4500}
      onClose={handleCloseError}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert onClose={handleCloseError} severity="error" sx={{ width: "100%" }}>
        {error.error + ": " + error.message}
      </Alert>
    </Snackbar>}

    {/* Loading */}
    <Dialog open={Boolean(loading)}>
      <DialogContent sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
      }}>
        <CircularProgress />
        <DialogContentText>
          {loading}
        </DialogContentText>
      </DialogContent>
    </Dialog>

    {/* Importar Arquivos */}
    <ImportGeometry
      open={Boolean(importType)}
      type={importType}
      onClose={() => setImportType(undefined)}
      onFinish={handleOnImportGeometry}
    />

    {/* Tela */}
    <Dialog fullScreen {...modalProps}>
      <Box sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: toolbarPosition === "column" ? "row" : "column",
      }}>
        {/* Barra de ferramentas */}
        <Box sx={{
          gap: "7px",
          width: toolbarPosition === "column" ? "40px" : "100%",
          height: toolbarPosition === "row" ? "40px" : "100%",
          display: "flex",
          paddingY: toolbarPosition === "column" ? "15px" : undefined,
          paddingX: toolbarPosition === "row" ? "15px" : undefined,
          alignItems: "center",
          flexDirection: toolbarPosition,
          boxSizing: "border-box",
          backgroundColor: "secondary.main",
        }}>
          <ItemButton
            tooltip="Fechar"
            onClick={() => { if (modalProps.onClose) modalProps.onClose({}, "escapeKeyDown"); }}
            icon={<Close {...iconProps} />}
          />

          <div style={{ flex: 1 }} />

          <ItemButton
            tooltip="Inportar GeoJson"
            onClick={() => handleImportGeometry("json")}
            icon={<JavascriptTwoTone {...iconProps} />}
          />
          <ItemButton
            tooltip="Inportar .shp"
            onClick={() => handleImportGeometry("shp")}
            icon={<PolylineOutlined {...iconProps} />}
          />
          <ItemButton
            tooltip="Inportar .zip"
            onClick={() => handleImportGeometry("zip")}
            icon={<FolderZipOutlined {...iconProps} />}
          />
          <Divider2 position={toolbarPosition === "column" ? "horizontal" : "vertical"} color={"#000"} />

          {/* Área de Drenagem */}
          {delineateWatershed && <>
            <ItemButton
              tooltip="Área de Drenagem"
              onClick={() => handleWatershed(-19.9272087, -43.9278269)}
              icon={<RoundedCornerOutlined {...iconProps} />}
            />
            <Divider2 position={toolbarPosition === "column" ? "horizontal" : "vertical"} color={"#000"} />
          </>}

          <ItemButton
            tooltip="Novo Circulo"
            onClick={handleNewCircle}
            icon={<CircleOutlined {...iconProps} />}
          />
          <ItemButton
            tooltip="Novo BBOX"
            onClick={handleNewBBOX}
            icon={<RectangleOutlined {...iconProps} />}
          />
          <ItemButton
            tooltip="Novo Polígono"
            onClick={handleNewPolygon}
            icon={<HexagonOutlined {...iconProps} />}
          />
          <ItemButton
            tooltip="Nova Linha"
            onClick={handleNewPolyline}
            icon={<Timeline {...iconProps} />}
          />
          <ItemButton
            tooltip="Novo Marcador"
            onClick={handleNewMarker}
            icon={<PinDrop {...iconProps} />}
          />
          <ItemButton
            tooltip="Novo Marcador Circular"
            onClick={handleNewCircleMarker}
            icon={<MyLocation {...iconProps} />}
          />
          <ItemButton
            tooltip="Novo Texto"
            onClick={handleNewText}
            icon={<Title {...iconProps} />}
          />

          {/* Posição da Barra */}
          <div style={{ flex: 1 }} />
          <ItemButton
            tooltip="Posição da Barra de Ferramentas"
            onClick={() => setToolbarPosition(toolbarPosition === "column" ? "row" : "column")}
            icon={<ChangeCircleOutlined {...iconProps} />}
          />
        </Box>

        {/* Mapa e informações */}
        <Box sx={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
        }}>
          {/* Layers e Mapa */}
          <Paper sx={{
            flex: 1,
            margin: "7px",
            display: "flex",
            flexDirection: "row",
            padding: "7px",
            boxSizing: "inherit",
            gap: "15px",
          }}>
            {/* Layers */}
            <Box sx={{
              display: "flex",
              flexDirection: "column",
              width: "250px",
            }}>
              <Typography>
                Layers:
              </Typography>
              <HidroLayersComponent
                hidroLayers={layers.current}
              />
            </Box>

            <Divider orientation="vertical" />
            {/* Mapa */}
            <HidroMap
              zoomBtn
              screenshot
              expandable
              recenter
              chooseMap
              onCreateMap={setMap}
              buttonPosition="bottomRight"
              hidroLayers={layers.current}
            />
          </Paper>


          {/* Informações e Mais */}
          <Paper sx={{
            margin: "7px",
            width: "25vw",
            display: "flex",
            flexDirection: "column",
          }}>

            {/* Área de Drenagem */}
            {watershedPoint && <Box sx={{
              display: "flex",
              flexDirection: "column",
              gap: "15px",
              padding: "15px",
              boxSizing: "content-box",
            }}>
              <Box sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}>
                <Typography>
                  Delimitar Área de Drenagem
                </Typography>
                <div style={{ flex: 1 }} />
                <IconButton onClick={() => handleWatershed(0, 0, true)}>
                  <Close />
                </IconButton>
              </Box>
              <Box sx={{
                display: "flex",
                flexDirection: "row",
                gap: "15px",
                width: "100%",
              }}>
                <NumberInput
                  placeholder="Latitude"
                  value={watershedPoint?.lat}
                  onChange={(val) => handleWatershed(val, watershedPoint?.lng ?? -20)}
                  step={0.01}
                />
                <NumberInput
                  placeholder="Longitude"
                  value={watershedPoint?.lng}
                  onChange={(val) => handleWatershed(watershedPoint?.lat ?? -40, val)}
                  step={0.01}
                />
              </Box>
              <Button autoFocus variant="contained" onClick={handleDelineateWatershed}>
                Delimitar
              </Button>
            </Box>}

            {/* Barra de ferramentas */}
            <Box sx={{
              gap: "7px",
              width: "100%",
              height: "40px",
              display: "flex",
              paddingX: "15px",
              alignItems: "center",
              flexDirection: "row",
              boxSizing: "border-box",
              backgroundColor: "secondary.main",
            }}>
              <Typography color={theme.palette.getContrastText(theme.palette.secondary.main)} >
                {geomInformation?.name}
              </Typography>
              <div style={{ flex: 1 }} />

              <ItemButton
                disable={!geomEditing}
                tooltip="Editar Geometria"
                onClick={handleEdit}
                icon={<Edit
                  {...(geomEditing ? (isEditing ? editingIconProps : iconProps) : disableIconProps)}
                />}
              />
              <ItemButton
                disable={!geomEditing}
                tooltip="Salvar Geometria"
                onClick={handleSave}
                icon={<Save
                  {...(geomEditing ? iconProps : disableIconProps)}
                />}
              />
              <ItemButton
                disable={!geomEditing}
                tooltip="Excluir Geometria"
                onClick={handleRemove}
                icon={<Delete
                  {...(geomEditing ? iconProps : disableIconProps)}
                />}
              />
            </Box>

            {/* Informações da Geometria */}
            <GeomInformation
              layer={geomInformation}
              editing={isEditing}
            />

          </Paper>

        </Box>
      </Box>

      {onFinish && <DialogActions>
        <Button onClick={() => { if (onFinish) onFinish(layers.current); }}>
          Finalizar
        </Button>
      </DialogActions>}
    </Dialog >
  </>;
};


const Divider2: React.FC<{ color?: string; position?: "vertical" | "horizontal"; }> = ({ color, position }) => {
  return <div
    style={{
      opacity: 1,
      backgroundColor: color,
      width: position === "horizontal" ? "100%" : "1px",
      height: position === "vertical" ? "100%" : "1px",
    }}
  />;
};

interface ItemButtonProps {
  color?: string;
  disable?: boolean;
  tooltip?: string;
  icon?: JSX.Element;
  onClick?: () => void;
}

const ItemButton: React.FC<ItemButtonProps> = ({
  disable,
  tooltip,
  icon,
  onClick
}) => {
  return (
    <Tooltip
      arrow
      title={<Typography fontSize={14}>{tooltip}</Typography>}>
      <span>
        <IconButton
          disabled={disable}
          onClick={onClick}
          sx={{
            borderRadius: "2px",
            width: "26px",
            height: "26px",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};


const GeomInformation: React.FC<{ layer?: HidroLayer; editing?: boolean; }> = ({ layer, editing }) => {
  // TODO: Alterar Geometrias pelos atributos
  // let geometryItems: JSX.Element | JSX.Element[] = [];
  // if (layer?.layer instanceof CircleMarker) {
  //   const geom = layer.layer as CircleMarker;
  //   geometryItems = <Box sx={{
  //     gap: "7px",
  //     paddingY: "15px",
  //     display: "flex",
  //     flexDirection: "column",
  //   }}>
  //     <NumberInput
  //       placeholder="Raio"
  //       value={geom.getRadius()}
  //       onChange={(val) => geom.setRadius(val)}
  //     />
  //     <NumberInput
  //       placeholder="Latitude"
  //       value={geom.getLatLng().lat}
  //       onChange={(val) => geom.setLatLng(new LatLng(
  //         val,
  //         geom.getLatLng().lng,
  //         geom.getLatLng().alt
  //       ))}
  //     />
  //     <NumberInput
  //       placeholder="Longitude"
  //       value={geom.getLatLng().lng}
  //       onChange={(val) => geom.setLatLng(new LatLng(
  //         geom.getLatLng().lat,
  //         val,
  //         geom.getLatLng().alt
  //       ))}
  //     />
  //   </Box>;
  // }
  // else if (layer?.layer instanceof Rectangle) {
  //   const geom = layer.layer;
  //   geom.getBounds();
  //   // geom.setBounds();

  // } else if (layer?.layer instanceof Polyline) {
  //   const geom = layer.layer;
  //   geom.getLatLngs();
  //   // geom.setLatLngs();
  // }

  const propertieItems: JSX.Element[] = [];
  if (layer?.properties) {
    let i = 0;
    for (const key in layer.properties) {
      propertieItems.push(<Box key={key} sx={{
        width: "100%",
        display: 'flex',
        flexDirection: "row",
      }}>
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: (i % 2 === 0) ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.1)",
        }}>
          <Typography fontWeight={"bold"} >
            {key}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: (i % 2 === 0) ? "rgba(0, 0, 0, 0.1)" : "rgba(0, 0, 0, 0.3)",
        }}>
          <Typography>
            {layer?.properties[key]}
          </Typography>
        </Box>
      </Box>);
      i++;
    }
  }

  return <Box sx={{
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    overflowX: "auto",
  }}>
    <SimpleTreeView>
      {/* <TreeItem itemId="geomdata" label="Geometria" disabled={!Boolean(layer?.layer)}>
        {geometryItems}
      </TreeItem> */}
      <TreeItem itemId="properties" label="Propriedades" disabled={(layer?.layer === undefined)}>
        {propertieItems}
      </TreeItem>
    </SimpleTreeView>
  </Box>;
};